import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Scales, Vault } from "@phosphor-icons/react";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1520440229-6469a149ac59?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Accesso effettuato");
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-zinc-50 text-left">
      {/* Left / brand */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-zinc-950 text-white overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15 grayscale" />
        <div className="relative z-10 flex items-center gap-2">
          <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Certy Trace" className="w-10 h-10 object-contain" />
          <span className="font-heading font-black text-xl tracking-tight">Certy Trace</span>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-heading text-4xl font-extrabold tracking-tight leading-tight">
            Due diligence conforme, per definizione.
          </h1>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            Intelligence su imprese e persone (contesto professionale) sotto Legittimo Interesse — Art. 6.1.f GDPR.
          </p>
          <div className="mt-8 space-y-3 text-sm">
            {[
              [Scales, "Proportionality Gate obbligatorio prima di ogni ricerca"],
              [Vault, "Registro di audit immutabile con hash-chain SHA-256"],
              [ShieldCheck, "Trasparenza delle fonti: InfoCamere · Gazzetta · Tribunali"],
            ].map(([Icon, t], i) => (
              <div key={i} className="flex items-center gap-3 text-zinc-300">
                <Icon size={18} weight="duotone" className="text-white shrink-0" />
                {t}
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          Mercato MVP: Italia · Dati registri simulati
        </div>
      </div>

      {/* Right / form */}
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Certy Trace" className="w-9 h-9 object-contain" />
            <span className="font-heading font-black text-lg">Certy Trace</span>
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950">Accedi alla piattaforma</h2>
          <p className="text-sm text-zinc-500 mt-1 mb-6">Inserisci le credenziali del tuo account professionale.</p>

          {error && (
            <div data-testid="login-error" className="mb-4 border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase font-mono tracking-wide text-zinc-600">Email</Label>
              <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="mt-1.5 border-zinc-300 rounded-sm" placeholder="nome@studio.it" />
            </div>
            <div>
              <Label className="text-xs uppercase font-mono tracking-wide text-zinc-600">Password</Label>
              <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="mt-1.5 border-zinc-300 rounded-sm" placeholder="••••••••" />
            </div>
          </div>

          <Button type="submit" disabled={loading} data-testid="login-submit"
            className="w-full mt-6 bg-zinc-950 text-white hover:bg-zinc-800 rounded-sm">
            {loading ? "Accesso…" : "Accedi"}
          </Button>

          <p className="text-sm text-zinc-500 mt-6 text-center">
            Non hai un account?{" "}
            <Link to="/register" data-testid="goto-register" className="text-zinc-950 font-medium underline underline-offset-2">
              Registrati
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
